import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, map, type Subscription } from 'rxjs';

import { isClientAccount, type User } from '../../../../shared/models/user.model';
import { AuthService } from '../../../../shared/services/auth.service';
import { TokenService } from '../../../../shared/services/token.service';
import { ButtonComponent, InputComponent } from '../../../../shared/ui';
import {
  ACCOUNT_MAIL_WAIT_S,
  type AccountMailNotice,
  AccountMailCooldownStore,
  accountMailNotice,
} from '../../data/account-mail-cooldown.store';
import {
  type ProfilePersonalData,
  type ProfileSettingsFailure,
  ProfileSettingsService,
} from '../../data/profile-settings.service';
import { PROFILE_COPY } from '../../profile.copy';
import { MailCooldownButtonComponent } from '../mail-cooldown-button/mail-cooldown-button.component';

type PersonalField = 'name' | 'surnames' | 'phone';
const PERSONAL_FIELDS: readonly PersonalField[] = ['name', 'surnames', 'phone'];

/** Avisos, errores y peticiones en vuelo de la pestaña. Todo de UNA cuenta (`owner`). */
interface SettingsState {
  owner: number | null;
  saving: boolean;
  fieldErrors: Partial<Record<PersonalField, string>>;
  personalNotice: AccountMailNotice | null;
  emailSaving: boolean;
  emailError: string | null;
  emailNotice: AccountMailNotice | null;
  passwordNotice: AccountMailNotice | null;
  resendNotice: AccountMailNotice | null;
}

const EMPTY_STATE: SettingsState = {
  owner: null,
  saving: false,
  fieldErrors: {},
  personalNotice: null,
  emailSaving: false,
  emailError: null,
  emailNotice: null,
  passwordNotice: null,
  resendNotice: null,
};

/** Cuenta y token de sesión con los que se hidrataron los formularios (H1 de la Astra p1). */
interface FormOwner {
  id: number | null;
  token: string | null;
}

/** El correo se compara y se manda recortado y en minúsculas. */
const normalizeEmail = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

/** Los datos personales del formulario tal como se mandan: recortados y `phone` vacío como `null`. */
function personalData(raw: Record<PersonalField, string>): ProfilePersonalData {
  return { name: raw.name.trim(), surnames: raw.surnames.trim(), phone: raw.phone.trim() || null };
}

function personalFormValue(source: Partial<Record<PersonalField, string | null | undefined>> | null) {
  return { name: source?.name ?? '', surnames: source?.surnames ?? '', phone: source?.phone ?? '' };
}

/**
 * Ajustes del perfil (`/perfil/ajustes`, W3, §5.6.5 de B10): datos personales (`PUT /profile`), correo
 * (`PUT /users/{id}`), enlace para cambiar la contraseña (`GET /restore-password`) y, para cuentas sin verificar, el
 * reenvío del correo de verificación. **Solo cuentas `cliente`** (B20): el resto ve `CLIENT_ONLY` y no pide nada.
 * Fuera de ajustes (B25): dirección, provincia, cantón, avatar, juegos, newsletter, notificaciones y borrar la cuenta.
 *
 * **El estado pertenece a la cuenta.** Al cambiar `currentUser()?.id` (A → B, con o sin `null` en medio) se cancelan
 * las peticiones en vuelo, se vacían avisos y errores y los formularios se rehidratan con la cuenta nueva. Además,
 * cada aviso lleva la cuenta que lo pidió y una respuesta que vuelve con otra cuenta dentro no escribe nada, aunque
 * el efecto aún no haya corrido. Un cambio de `currentUser` de la MISMA cuenta (el `patch` propio) no rehidrata: solo
 * se recarga el bloque que acaba de guardar, sin pisar lo que se escribe en el otro.
 *
 * **Los valores de los formularios también tienen dueño** (H1 de la Astra p1): `formOwner` guarda la cuenta y el token
 * con los que se hidrataron, y solo `hydrate()` lo fija. Guardar datos o cambiar el correo no pide nada si la cuenta de
 * la sesión ya es otra (un submit entre el login de B y el efecto mandaría lo escrito por A con el token de B) o si el
 * token ya no es el de la hidratación (otra pestaña entró con otra cuenta: el interceptor lee el token de
 * `localStorage` en cada petición y `PUT /profile` saca la cuenta del token). En ese segundo caso se lanza `auth.me()`
 * para que la pestaña se resincronice, sin aviso; con la cuenta nueva el efecto rehidrata y, si vuelve la misma, se
 * rehidrata aquí con el token nuevo.
 */
@Component({
  selector: 'app-profile-settings',
  imports: [ReactiveFormsModule, InputComponent, ButtonComponent, MailCooldownButtonComponent],
  templateUrl: './profile-settings.component.html',
  styleUrl: './profile-settings.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSettingsComponent {
  private readonly auth = inject(AuthService);
  private readonly tokens = inject(TokenService);
  private readonly settings = inject(ProfileSettingsService);
  private readonly mail = inject(AccountMailCooldownStore);
  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly copy = PROFILE_COPY.SETTINGS;
  protected readonly unverifiedText = PROFILE_COPY.HEADER.UNVERIFIED_EMAIL;

  protected readonly user = this.auth.currentUser;
  private readonly userId = computed(() => this.user()?.id ?? null);
  protected readonly isClient = computed(() => isClientAccount(this.user()));
  /** B24: solo `cliente` y solo con `false` explícito. */
  protected readonly showUnverified = computed(() => this.isClient() && this.user()?.verified === false);

  protected readonly personalForm = this.fb.group({ name: '', surnames: '', phone: '' });
  /** Grupo propio (no un control suelto): sin `[formGroup]` el `<form>` no tiene `ngSubmit` y el submit nativo recarga. */
  protected readonly emailForm = this.fb.group({ email: '' });
  private readonly emailControl = this.emailForm.controls.email;

  private readonly personalValue = toSignal(
    this.personalForm.valueChanges.pipe(map(() => this.personalForm.getRawValue())),
    { initialValue: this.personalForm.getRawValue() },
  );
  private readonly emailValue = toSignal(this.emailControl.valueChanges, { initialValue: this.emailControl.value });

  /** Dueño de los valores de los formularios. Solo lo escribe `hydrate()`. */
  private readonly formOwner = signal<FormOwner>({ id: null, token: null });
  /** Resincronización tras ver otro token (otra pestaña): una sola en vuelo. */
  private resync: Subscription | null = null;

  /** Mientras los formularios no sean de la cuenta de la sesión, sus botones quedan deshabilitados. */
  protected readonly formsOwned = computed(() => {
    const owner = this.formOwner().id;
    return owner !== null && owner === this.userId();
  });

  private readonly state = signal<SettingsState>(EMPTY_STATE);
  /** El estado, solo si es de la cuenta de ahora. */
  protected readonly view = computed<SettingsState>(() => {
    const state = this.state();
    return state.owner !== null && state.owner === this.userId() ? state : EMPTY_STATE;
  });

  private readonly subs: Record<'personal' | 'email' | 'password' | 'resend', Subscription | null> = {
    personal: null,
    email: null,
    password: null,
    resend: null,
  };

  /** «Guardar cambios» solo con cambios respecto a la cuenta (recortados). */
  protected readonly personalChanged = computed(() => {
    const current = personalFormValue(this.user());
    const typed = this.personalValue();
    return PERSONAL_FIELDS.some((field) => typed[field].trim() !== current[field].trim());
  });

  /** «Cambiar correo» solo si el correo (recortado y en minúsculas) es otro. */
  protected readonly emailChanged = computed(
    () => normalizeEmail(this.emailValue()) !== normalizeEmail(this.user()?.email),
  );

  protected readonly passwordText = computed(() =>
    this.copy.PASSWORD_TEXT.replace('{correo}', this.user()?.email ?? ''),
  );

  constructor() {
    effect(() => {
      const id = this.userId();
      untracked(() => this.resetForAccount(id));
    });
    inject(DestroyRef).onDestroy(() => {
      this.cancelAll();
      this.resync?.unsubscribe();
    });
  }

  // ── Datos personales ─────────────────────────────────────────────

  protected savePersonal(): void {
    const owner = this.userId();
    if (owner === null || !this.isClient() || this.view().saving || !this.personalChanged()) return;
    if (!this.formBelongsToSession()) return;

    const data = personalData(this.personalForm.getRawValue());
    const fieldErrors: Partial<Record<PersonalField, string>> = {};
    if (!data.name) fieldErrors.name = this.copy.NAME_REQUIRED;
    if (!data.surnames) fieldErrors.surnames = this.copy.SURNAMES_REQUIRED;
    if (Object.keys(fieldErrors).length > 0) {
      this.patch(owner, { fieldErrors, personalNotice: null });
      return;
    }

    this.patch(owner, { saving: true, fieldErrors: {}, personalNotice: null });
    this.subs.personal?.unsubscribe();
    this.subs.personal = this.settings
      .updateProfile(data)
      .pipe(finalize(() => this.patch(owner, { saving: false })))
      .subscribe({
        next: (res) => {
          if (this.userId() !== owner) return;
          this.auth.patchCurrentUser(owner, res.data);
          this.personalForm.reset(personalFormValue(res.data));
          this.patch(owner, { personalNotice: { tone: 'success', text: res.message ?? this.copy.SAVED } });
        },
        error: (failure: ProfileSettingsFailure) => {
          if (this.userId() !== owner) return;
          this.patch(owner, this.personalFailure(failure));
        },
      });
  }

  private personalFailure(failure: ProfileSettingsFailure): Partial<SettingsState> {
    if (failure.kind === 'validation') {
      const fieldErrors: Partial<Record<PersonalField, string>> = {};
      for (const field of PERSONAL_FIELDS) {
        if (failure.fields[field]) fieldErrors[field] = failure.fields[field];
      }
      if (Object.keys(fieldErrors).length > 0) return { fieldErrors };
    }
    if (failure.kind === 'forbidden') {
      return { personalNotice: { tone: 'error', text: failure.message || this.copy.CLIENT_ONLY } };
    }
    return { personalNotice: { tone: 'error', text: this.copy.SAVE_ERROR } };
  }

  // ── Correo ───────────────────────────────────────────────────────

  protected changeEmail(): void {
    const owner = this.userId();
    if (owner === null || !this.isClient() || this.view().emailSaving) return;
    if (!this.formBelongsToSession()) return;

    const email = normalizeEmail(this.emailControl.value);
    // Sin cambio, sin petición: un PUT del correo actual de una cuenta sin verificar reenvía la verificación.
    if (email === normalizeEmail(this.user()?.email)) return;
    // Ni vacío ni con formato inválido: `email` no es `required` en la API y un "" acaba en 500.
    if (!email || Validators.email(new FormControl(email)) !== null) {
      this.patch(owner, { emailError: this.copy.EMAIL_INVALID, emailNotice: null });
      return;
    }

    this.patch(owner, { emailSaving: true, emailError: null, emailNotice: null });
    this.subs.email?.unsubscribe();
    // [D3] 1219007501006596: reintentar con el mismo correo nuevo cuando la API ya lo había guardado (p. ej. tras un
    // 5xx con el correo cambiado) responde 200 y VUELVE a mandar la verificación, sin pasar por `account-mail`. La web
    // no lo evita: tras el 5xx el input conserva lo tecleado y `currentUser` el correo viejo, así que el botón sigue
    // activo.
    this.subs.email = this.settings
      .changeEmail(owner, email)
      .pipe(finalize(() => this.patch(owner, { emailSaving: false })))
      .subscribe({
        next: (res) => {
          if (this.userId() !== owner) return;
          // La API deja la cuenta sin verificar: aparecen el aviso y los dos «Reenviar», y «Mis pedidos» bloquea (B44).
          this.auth.patchCurrentUser(owner, { email: res.email, verified: false, email_verified_at: undefined });
          // AL1: la API ya mandó el correo de verificación al guardar: «Reenviar» (ajustes y cabecera) espera 60 s.
          this.mail.start('resend', ACCOUNT_MAIL_WAIT_S);
          this.emailControl.reset(res.email);
          this.patch(owner, {
            emailNotice: { tone: 'success', text: this.copy.EMAIL_CHANGED.replace('{nuevo}', res.email) },
          });
        },
        error: (failure: ProfileSettingsFailure) => {
          if (this.userId() !== owner) return;
          // Un 5xx puede llegar con el correo YA cambiado (el correo de verificación sale tras guardar): no se asume
          // nada y no se parchea `currentUser`.
          this.patch(owner, this.emailFailure(failure));
        },
      });
  }

  private emailFailure(failure: ProfileSettingsFailure): Partial<SettingsState> {
    if (failure.kind === 'validation') {
      // Solo el correo en uso trae copy de la casa (A3-bis); cualquier otro 422 —el de formato sale en inglés, y puede
      // llegar sin `error.email` (H3 de la Astra p1)— es EMAIL_INVALID.
      const taken = failure.fields['email'] === this.copy.EMAIL_TAKEN;
      return { emailError: taken ? this.copy.EMAIL_TAKEN : this.copy.EMAIL_INVALID };
    }
    return { emailNotice: { tone: 'error', text: this.copy.EMAIL_ERROR } };
  }

  // ── Contraseña y reenvío (espera compartida, B24/B43) ────────────

  protected sendResetLink(): void {
    const user = this.user();
    if (!user || !this.isClient()) return;
    const owner = user.id;
    this.subs.password?.unsubscribe();
    this.subs.password = this.mail.send('reset', this.auth.forgotPassword(user.email)).subscribe((outcome) => {
      this.patch(owner, { passwordNotice: accountMailNotice(outcome, this.copy.PASSWORD_SENT) });
    });
  }

  protected resendVerification(): void {
    const owner = this.userId();
    if (owner === null || !this.showUnverified()) return;
    this.subs.resend?.unsubscribe();
    this.subs.resend = this.mail.send('resend', this.auth.resendVerificationEmail(owner)).subscribe((outcome) => {
      this.patch(owner, { resendNotice: accountMailNotice(outcome, this.copy.RESEND_SENT) });
    });
  }

  // ── Atado a la cuenta ────────────────────────────────────────────

  /** Escribe en el estado de `owner`, solo si sigue siendo la cuenta de la sesión. */
  private patch(owner: number, changes: Partial<SettingsState>): void {
    if (this.userId() !== owner) return;
    this.state.update((state) => ({ ...(state.owner === owner ? state : { ...EMPTY_STATE, owner }), ...changes }));
  }

  private resetForAccount(id: number | null): void {
    this.cancelAll();
    this.state.set({ ...EMPTY_STATE, owner: id });
    this.hydrate(this.user());
  }

  private hydrate(user: User | null): void {
    this.personalForm.reset(personalFormValue(user));
    this.emailControl.reset(user?.email ?? '');
    this.formOwner.set({ id: user?.id ?? null, token: this.tokens.getUserToken() });
  }

  /**
   * H1: ¿los valores de los formularios son de la sesión con la que saldría la petición? Con otra cuenta en
   * `currentUser` (aún sin rehidratar) no. Con otro token (otra pestaña cambió la sesión) tampoco, y además se
   * resincroniza la pestaña con `auth.me()`.
   */
  private formBelongsToSession(): boolean {
    const owner = this.formOwner();
    if (owner.id === null || owner.id !== this.userId()) return false;
    if (owner.token !== this.tokens.getUserToken()) {
      this.resyncSession();
      return false;
    }
    return true;
  }

  /**
   * `me()` carga la cuenta del token nuevo (el efecto rehidrata si es otra) o limpia la sesión. Si vuelve la MISMA
   * cuenta el id no cambia y el efecto no corre: se rehidrata aquí, con el token nuevo.
   *
   * Solo con la respuesta VIGENTE (H1-p2 de la Astra p2): se usa lo que emite `me()`, no `currentUser`. `me()` emite
   * `null` cuando descarta una respuesta vieja o se cancela (login o logout en esta pestaña), y entonces `currentUser`
   * aún es la cuenta anterior: rehidratarla ataría sus valores al token que haya ahora, de otra sesión. Se rehidrata
   * solo si el usuario emitido es la misma cuenta del dueño, la sesión sigue en ella y el token no ha vuelto a cambiar
   * desde que se pidió. Si no, nada: otra cuenta la hidrata el efecto, y un nuevo cambio de token lo detecta
   * `formBelongsToSession()` en el siguiente envío.
   */
  private resyncSession(): void {
    if (this.resync && !this.resync.closed) return;
    const before = this.formOwner().id;
    const token = this.tokens.getUserToken();
    this.resync = this.auth.me().subscribe((user) => {
      const current =
        user !== null && user.id === before && this.userId() === before && this.tokens.getUserToken() === token;
      if (current) this.hydrate(user);
    });
  }

  private cancelAll(): void {
    for (const key of Object.keys(this.subs) as (keyof typeof this.subs)[]) {
      this.subs[key]?.unsubscribe();
      this.subs[key] = null;
    }
  }
}
